import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { Input } from '@repo/videa-components';
import { useAtom, useAtomValue } from 'jotai';
import { useInView } from 'react-intersection-observer';

import { VideaTypography } from '@/common-components/elements/VideaTypography/VideaTypography';
import { useVoiceNoteTemplates } from '@/react-query/videa-notes/useVoiceNoteTemplates';

import {
  ambientViewTypeAtom,
  jumpToCurrentApptAtom,
  recentNotesViewAtom,
  searchAtom,
  selectDateAtom,
  templatesSearchAtom,
} from '../patientList/atoms';
import { PatientListPageLayout } from '../patientList/PatientListPageLayout';
import { PatientListPageLayoutContextMap } from '../patientList/types';
import { getNextAppointmentByStandalonePatientVisitId } from '../patientList/useScrollAppointmentIntoView';

import { AmbientTable } from './AmbientTable';
import { AmbientViewTabs } from './AmbientViewTabs';
import { useAmbientAppointmentsData } from './hooks/useAmbientAppointmentsData';
import { useAmbientInfiniteScroll } from './hooks/useAmbientInfiniteScroll';
import { useAmbientSorting } from './hooks/useAmbientSorting';
import { TemplatesTable } from './TemplatesTable';

export const AmbientPage = () => {
  // atoms
  const [selectedDate, setSelectedDate] = useAtom(selectDateAtom);
  const [templatesSearch, setTemplatesSearch] = useAtom(templatesSearchAtom);
  const ambientViewType = useAtomValue(ambientViewTypeAtom);
  const jumpToCurrentAppt = useAtomValue(jumpToCurrentApptAtom);
  const search = useAtomValue(searchAtom);
  const isRecentNotesView = useAtomValue(recentNotesViewAtom);

  // hooks
  const { t } = useLingui();
  const { inView: isNextAppointmentInView } = useInView();

  // refs
  const nextAppointmentRef = useRef<HTMLTableRowElement>(null);

  // Ambient-specific hooks (for Notes view)
  const {
    appointmentsWithTreatmentsAndNotes,
    isLoading,
    isError,
    hasNextPage,
    isAnyBatchLoading,
    fetchNextPage,
    invalidateNotesCache,
  } = useAmbientAppointmentsData();

  const { sortedAppointments, sortField, sortDirection, handleSort } =
    useAmbientSorting(appointmentsWithTreatmentsAndNotes, isRecentNotesView);

  // Templates-specific hooks (for Templates view)
  const {
    data: templatesData = [],
    isLoading: isLoadingTemplates,
    isError: isTemplatesError,
  } = useVoiceNoteTemplates();

  // Templates sorting state
  const [templatesSortField, setTemplatesSortField] = useState('name');
  const [templatesSortDirection, setTemplatesSortDirection] = useState<
    'asc' | 'desc'
  >('asc');

  const filteredAndSortedTemplates = useMemo(() => {
    // First filter by templates search term
    const searchLower = templatesSearch.toLowerCase().trim();
    const filtered = searchLower
      ? templatesData.filter((template) =>
          template.name?.toLowerCase().includes(searchLower),
        )
      : templatesData;

    // Then sort
    const sorted = [...filtered];
    sorted.sort((a, b) => {
      let aVal: string = '';
      let bVal: string = '';

      switch (templatesSortField) {
        case 'name':
          aVal = a.name || '';
          bVal = b.name || '';
          break;
        case 'type':
          aVal = a.type || '';
          bVal = b.type || '';
          break;
        case 'creator':
          aVal = a.createdBy?.name || '';
          bVal = b.createdBy?.name || '';
          break;
        default:
          return 0;
      }

      const comparison = aVal.localeCompare(bVal);
      return templatesSortDirection === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [
    templatesData,
    templatesSortField,
    templatesSortDirection,
    templatesSearch,
  ]);

  const handleTemplatesSort = (field: string, direction: 'asc' | 'desc') => {
    setTemplatesSortField(field);
    setTemplatesSortDirection(direction);
  };

  const nextAppointmentStandalonePatientVisitId =
    getNextAppointmentByStandalonePatientVisitId({
      appointments: sortedAppointments,
      selectedDate,
    });

  // Calculate indices using useMemo
  const { nextAppointmentIndex } = useMemo(() => {
    return {
      nextAppointmentIndex: sortedAppointments.findIndex(
        (appointment) =>
          appointment.appointment.standalonePatientVisitId ===
          nextAppointmentStandalonePatientVisitId,
      ),
    };
  }, [sortedAppointments, nextAppointmentStandalonePatientVisitId]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { ref: sentinelRef } = useAmbientInfiniteScroll(
    hasNextPage,
    isLoading,
    fetchNextPage,
    scrollContainerRef,
  );

  const scrollNextAppointmentIntoView = useCallback(() => {
    if (nextAppointmentIndex >= 0) {
      nextAppointmentRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [nextAppointmentIndex, nextAppointmentRef]);

  // Set today as default date and reset when search is cleared
  // Skip this behavior when in recent notes view mode
  useEffect(() => {
    if (!isRecentNotesView && search === '' && !selectedDate) {
      setSelectedDate(new Date());
    }
  }, [search, selectedDate, setSelectedDate, isRecentNotesView]);

  useEffect(() => {
    if (jumpToCurrentAppt === 'true') {
      scrollNextAppointmentIntoView();
    }
  }, [jumpToCurrentAppt, scrollNextAppointmentIntoView]);

  return (
    <div className="vai-flex vai-flex-col vai-h-screen vai-w-screen vai-overflow-hidden">
      <AmbientViewTabs />

      {ambientViewType === 'templates' ? (
        <div className="vai-flex-1 vai-flex vai-flex-col vai-overflow-hidden">
          {/* Templates Topbar */}
          <div className="vai-bg-white vai-px-4 vai-shadow-sm vai-flex vai-items-center vai-h-16">
            <div className="vai-w-80 vai-relative">
              <FontAwesomeIcon
                icon={faSearch}
                className="vai-absolute vai-left-3 vai-top-1/2 vai-transform vai--translate-y-1/2 vai-text-gray-400 vai-h-4 vai-w-4 vai-z-10"
              />
              <Input
                type="text"
                placeholder={t`Search templates`}
                value={templatesSearch}
                onChange={(e) => setTemplatesSearch(e.target.value)}
                className="vai-pl-10"
              />
            </div>
          </div>
          {/* Templates Content */}
          <div className="vai-flex-1 vai-flex vai-flex-col vai-bg-gray-100 vai-p-4 vai-overflow-hidden">
            {isTemplatesError ? (
              <div className="vai-flex vai-justify-center vai-items-center vai-p-8">
                <div className="vai-text-red-500">
                  <Trans>Error loading templates</Trans>
                </div>
              </div>
            ) : isLoadingTemplates ? (
              <div className="vai-flex vai-justify-center vai-items-center vai-p-8">
                <div className="vai-text-gray-600">
                  <Trans>Loading templates...</Trans>
                </div>
              </div>
            ) : filteredAndSortedTemplates.length === 0 ? (
              <VideaTypography
                variant="headingExtrasmall"
                className="vai-w-full vai-text-center vai-p-10 vai-text-zinc-700"
                as="h3"
              >
                {templatesSearch ? (
                  <Trans>No templates found matching your search.</Trans>
                ) : (
                  <Trans>No templates available.</Trans>
                )}
              </VideaTypography>
            ) : (
              <TemplatesTable
                templates={filteredAndSortedTemplates}
                onSort={handleTemplatesSort}
                sortField={templatesSortField}
                sortDirection={templatesSortDirection}
                scrollContainerRef={scrollContainerRef}
                sentinelRef={sentinelRef}
              />
            )}
          </div>
        </div>
      ) : (
        // Notes View - default
        <PatientListPageLayout
          context={PatientListPageLayoutContextMap.Ambient}
          allAppointments={sortedAppointments}
          isLoadingAppointments={isLoading || isAnyBatchLoading}
          isLoadingVisitSummaries={false}
          nextAppointmentIndex={nextAppointmentIndex}
          isNextAppointmentInView={isNextAppointmentInView}
          onScrollNextAppointmentIntoView={scrollNextAppointmentIntoView}
        >
          <div className="vai-flex vai-flex-col vai-h-[87vh]">
            {isError ? (
              <div className="vai-flex vai-justify-center vai-items-center vai-p-8">
                <div className="vai-text-red-500">
                  <Trans>Error loading appointments</Trans>
                </div>
              </div>
            ) : (
              <AmbientTable
                appointments={sortedAppointments}
                onSort={handleSort}
                sortField={sortField}
                sortDirection={sortDirection}
                invalidateNotesCache={invalidateNotesCache}
                scrollContainerRef={scrollContainerRef}
                sentinelRef={sentinelRef}
                nextAppointmentRef={nextAppointmentRef}
                isRecentNotesView={isRecentNotesView}
              />
            )}
          </div>
        </PatientListPageLayout>
      )}
    </div>
  );
};
